// src/hooks/purchases/usePurchaseDtePreview.ts
import { useState } from 'react';
import { blobToBase64 } from '../../utils/blobToBase64';
import useLoading from '../useLoading';
import PurchasePdfService from '../../services/purchases/pdf/PurchasePdfService';
import { useBasePurchase } from './useBasePurchase';

/**
 * 📄 Hook para obtener el PDF DTE de una compra (incluso anulada), codificado en base64.
 *
 * ✅ Usa `PurchasePdfService.view` internamente
 * ✅ Conversión segura de Blob → Base64
 * ✅ Abre automáticamente el modal para visualizar el PDF
 */
export const usePurchaseDtePreview = () => {
  const [base64Dte, setBase64Dte] = useState<string | undefined>();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { openDtePdfModal } = useBasePurchase();

  /**
   * Obtiene el PDF de una compra por su ID y abre el modal de visualización.
   *
   * @param purchaseId ID de la compra
   */
  const handleViewDte = async (purchaseId: number) => {
    if (!purchaseId) {
      console.warn('⚠️ No se proporcionó el ID de la compra');
      return;
    }

    try {
      showLoadingIndicator();

      // ✅ Desestructurar correctamente
      const { blob } = await PurchasePdfService.view(purchaseId);

      if (!(blob instanceof Blob)) {
        console.error("❌ El archivo recibido no es un Blob válido:", blob);
        return;
      }

      const base64 = await blobToBase64(blob);
      setBase64Dte(base64);

      // ✅ Mostrar modal solo después de que el PDF esté cargado
      openDtePdfModal();

    } catch (err) {
      console.error('❌ Error al obtener el DTE de compra:', err);
    } finally {
      hideLoadingIndicator();
    }
  };

  return {
    base64Dte,
    setBase64Dte,
    handleViewDte,
  };
};
